import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Grid from "@mui/material/Grid/Grid";
import FormSection from "../../../../../common/components/functional/formSection/FormSection";
import { SimpleTable } from "../../../../../common/components/functional/simpleTable/SimpleTable";
import { useConfirmationDialog } from "../../../../../contexts/ConfirmationDialogContext";
import { FringeBenefitDialog } from "./FringeBenefitDialog";
import { FringeBenefitTableItem } from "./FringeBenefitTableItem";
import { FringeBenefitsHeader } from "./FringeBenefitsHeader";
import {
  ChangeFringeBenefits,
  FringeBenefit,
  FringeBenefitType,
} from "./types";
import {
  fetchFringeBenefitTypes,
  saveFringeBenefits,
} from "../../../../employment/employmentSlice";
import { useAppDispatch, useAppSelector } from "../../../../../app/hooks";
import { isFulfilled } from "@reduxjs/toolkit";
import {
  constructFringeBenefitPayload,
  mapApiTypesToFringeBenefits,
  mapFringeBenefitsToApiTypes,
} from "./mappers";

type Props = {
  canEdit: boolean;
};

export const FringeBenefits = ({ canEdit }: Props) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { confirm } = useConfirmationDialog();

  const employment = useAppSelector((state) => state.employment.additionalInfo);
  const version = employment?.details?.version;
  const fringeBenefits = mapApiTypesToFringeBenefits(
    employment?.details?.fringeBenefits || [],
  );

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [fringeBenefitTypes, setFringeBenefitTypes] = useState<
    FringeBenefitType[]
  >([]);
  const [selectedFringeBenefit, setSelectedFringeBenefit] =
    useState<FringeBenefit | null>(null);

  const closeDialog = () => {
    setIsDialogOpen(false);
    setSelectedFringeBenefit(null);
  };

  const onAddNewFringeBenefit = () => {
    setSelectedFringeBenefit(null);
    setIsDialogOpen(true);
  };

  const onEdit = (id: string) => {
    const fringeBenefit = fringeBenefits.find((benefit) => benefit.id === id);
    if (!fringeBenefit) return;
    setSelectedFringeBenefit(fringeBenefit);
    setIsDialogOpen(true);
  };

  const onCopy = (id: string) => {
    const fringeBenefit = fringeBenefits.find((benefit) => benefit.id === id);
    if (!fringeBenefit) return;
    setSelectedFringeBenefit({ ...fringeBenefit, id: "", endDate: "" });
    setIsDialogOpen(true);
  };

  const onDelete = async (id: string) => {
    const confirmed = await confirm({
      title: t("common:confirmDialogDelete"),
      contentText: t("talentProfile:confirmDeletingFringeBenefit"),
      confirmText: t("common:delete"),
      cancelText: t("common:cancel"),
      ConfirmButtonProps: { color: "error" },
    });
    if (!confirmed) return;

    const payload: ChangeFringeBenefits = {
      employmentId: employment?.employmentId,
      version,
      settings: mapFringeBenefitsToApiTypes(
        fringeBenefits.filter((benefit) => benefit.id !== id),
      ),
    };
    dispatch(saveFringeBenefits(payload)).then(
      (action) => isFulfilled(action) && closeDialog(),
    );
  };

  // Send updated data to backend (all fringe benefits)
  const onSave = (updatedFringeBenefit: FringeBenefit) => {
    const payload = constructFringeBenefitPayload(
      updatedFringeBenefit,
      fringeBenefits,
      employment?.employmentId,
      version,
    );
    dispatch(saveFringeBenefits(payload)).then(
      (action) => isFulfilled(action) && closeDialog(),
    );
  };

  useEffect(() => {
    dispatch(fetchFringeBenefitTypes({})).then((action) => {
      isFulfilled(action) && setFringeBenefitTypes(action.payload);
    });
  }, [dispatch]);

  return (
    <FormSection title={t("talentProfile:fringeBenefits")}>
      <Grid item container mt={3}>
        <SimpleTable
          header={<FringeBenefitsHeader />}
          items={fringeBenefits.map((benefit) => (
            <FringeBenefitTableItem
              key={benefit.id}
              fringeBenefit={benefit}
              canEdit={canEdit}
              onEdit={onEdit}
              onDelete={onDelete}
              onCopy={onCopy}
              fringeBenefitTypes={fringeBenefitTypes}
            />
          ))}
          addItem={onAddNewFringeBenefit}
          canAdd={canEdit}
        />
        <FringeBenefitDialog
          isOpen={isDialogOpen}
          onClose={closeDialog}
          onSave={onSave}
          fringeBenefits={fringeBenefits}
          selectedFringeBenefit={selectedFringeBenefit}
          fringeBenefitTypes={fringeBenefitTypes}
        />
      </Grid>
    </FormSection>
  );
};
